import { motion } from "framer-motion";
import {
  ShoppingCart,
  Flame,
  Wallet,
  QrCode,
  Globe,
  Boxes,
  CheckCircle2,
  Clock4,
  TrendingDown,
  AlertTriangle,
  Smartphone,
  Receipt,
  CreditCard,
  Banknote,
  Coffee,
  Pizza,
  Beer,
  Utensils,
  Wine,
  ChefHat,
  Soup,
  ShieldCheck,
  Zap,
} from "lucide-react";
import SlideShell from "@/components/SlideShell";
import Reveal from "@/components/Reveal";

/* ============ SHARED ============ */
const Eyebrow = ({ children, variant = "dark" }) => (
  <div
    className={`inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold ${
      variant === "light" ? "text-orange-300" : "text-emerald-800"
    }`}
  >
    <span
      className={`block w-6 h-px ${variant === "light" ? "bg-orange-300" : "bg-emerald-800"}`}
    />
    {children}
  </div>
);

const FeatureItem = ({ icon: Icon, children }) => (
  <li className="flex items-start gap-3 text-zinc-700 text-sm md:text-base">
    <span className="mt-0.5 grid place-items-center w-6 h-6 rounded-full bg-orange-500/10 text-orange-600 shrink-0">
      <Icon className="w-3.5 h-3.5" />
    </span>
    <span>{children}</span>
  </li>
);

const SectionHeading = ({ eyebrow, title, accentWord, lead }) => (
  <>
    <Reveal><Eyebrow>{eyebrow}</Eyebrow></Reveal>
    <Reveal delay={0.1}>
      <h2 className="font-display font-black mt-5 text-3xl md:text-5xl lg:text-[56px] leading-[1.02] tracking-tighter text-zinc-950">
        {title}{" "}
        {accentWord && <span className="text-emerald-800">{accentWord}</span>}
      </h2>
    </Reveal>
    {lead && (
      <Reveal delay={0.2}>
        <p className="mt-5 text-zinc-600 text-base md:text-lg leading-relaxed max-w-xl">
          {lead}
        </p>
      </Reveal>
    )}
  </>
);

const TwoCol = ({ id, index, total, reverse, bg = "white", children }) => (
  <SlideShell
    id={id}
    index={index}
    total={total}
    className={bg === "white" ? "bg-white" : "bg-zinc-100"}
  >
    <div
      className={`relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 pt-28 pb-20 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center ${
        reverse ? "lg:[&>*:first-child]:order-2" : ""
      }`}
    >
      {children}
    </div>
  </SlideShell>
);

/* ============ SLIDE 5 — POS ============ */

const POSMockup = () => (
  <div className="relative w-full max-w-md mx-auto">
    {/* tablet frame */}
    <div className="relative rounded-[28px] bg-zinc-950 p-3 shadow-2xl ring-1 ring-zinc-900/10">
      <div className="rounded-[20px] bg-zinc-50 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 bg-emerald-900 text-white">
          <div className="font-display font-bold text-sm">Mesa 12 · Salón A</div>
          <div className="text-[10px] uppercase tracking-[0.18em] opacity-70">Abierta</div>
        </div>
        <div className="p-4 space-y-2">
          {[
            { name: "Tacos al pastor", qty: 3, price: "$165.00", icon: Utensils },
            { name: "Coca-Cola 600ml", qty: 2, price: "$60.00", icon: Coffee },
            { name: "Quesadilla suiza", qty: 1, price: "$95.00", icon: Pizza },
          ].map((it, i) => (
            <motion.div
              key={it.name}
              initial={{ opacity: 0, x: -10 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: false }}
              transition={{ delay: 0.15 + i * 0.1, duration: 0.4 }}
              className="flex items-center justify-between bg-white rounded-xl px-3 py-2.5 ring-1 ring-zinc-200"
            >
              <div className="flex items-center gap-3">
                <div className="grid place-items-center w-8 h-8 rounded-lg bg-emerald-50 text-emerald-900">
                  <it.icon className="w-4 h-4" />
                </div>
                <div>
                  <div className="text-xs font-semibold text-zinc-950">{it.name}</div>
                  <div className="text-[10px] text-zinc-500">x{it.qty}</div>
                </div>
              </div>
              <div className="text-sm font-display font-bold text-zinc-950">{it.price}</div>
            </motion.div>
          ))}
          <div className="mt-2 flex items-center justify-between px-3 pt-3 border-t border-dashed border-zinc-300">
            <span className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">Total</span>
            <span className="font-display font-black text-xl text-emerald-900">$320.00</span>
          </div>
        </div>
        <div className="px-4 pb-4 pt-2 flex gap-2">
          <button className="flex-1 rounded-xl bg-zinc-950 text-white text-xs font-semibold py-2.5">
            Enviar a cocina
          </button>
          <button className="flex-1 rounded-xl bg-orange-500 text-white text-xs font-semibold py-2.5">
            Cobrar
          </button>
        </div>
      </div>
    </div>

    {/* Floating ticket */}
    <motion.div
      animate={{ y: [0, -8, 0] }}
      transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
      className="absolute -right-6 -bottom-6 hidden md:block bg-white rounded-2xl ring-1 ring-zinc-200 shadow-xl p-4 w-48"
    >
      <div className="flex items-center gap-2 text-emerald-700 text-[10px] uppercase tracking-[0.18em] font-bold">
        <Receipt className="w-3.5 h-3.5" /> Ticket impreso
      </div>
      <div className="mt-2 text-xs text-zinc-500">Mesa 12 · 3 productos</div>
      <div className="mt-1 font-display font-black text-zinc-950">$320.00</div>
    </motion.div>
  </div>
);

const POS = ({ id, index, total }) => (
  <TwoCol id={id} index={index} total={total} bg="white">
    <div className="lg:col-span-6">
      <SectionHeading
        eyebrow="05 · Punto de venta"
        title="Punto de venta para mesas, mostrador y"
        accentWord="delivery."
        lead="Vende más rápido y controla cada pedido desde el primer momento. Levanta pedidos en segundos y mantén la operación fluyendo."
      />
      <Reveal delay={0.3}>
        <ul className="mt-8 space-y-3.5">
          <FeatureItem icon={Utensils}>Mesas con plano del salón y unión de cuentas.</FeatureItem>
          <FeatureItem icon={ShoppingCart}>Mostrador rápido para taquerías, cafeterías y barras.</FeatureItem>
          <FeatureItem icon={Zap}>Pedidos para llevar y delivery integrados.</FeatureItem>
          <FeatureItem icon={Receipt}>Impresión de comandas y tickets automática.</FeatureItem>
        </ul>
      </Reveal>
    </div>
    <div className="lg:col-span-6">
      <Reveal delay={0.2}><POSMockup /></Reveal>
    </div>
  </TwoCol>
);

/* ============ SLIDE 6 — MONITOR DE COCINA ============ */

const KitchenMockup = () => (
  <div className="grid grid-cols-2 gap-3 max-w-lg mx-auto">
    {[
      { mesa: "Mesa 04", time: "00:32", items: ["Pasta carbonara", "Ensalada césar"], status: "preparando", color: "orange" },
      { mesa: "Mesa 11", time: "01:15", items: ["Rib eye 350g", "Papas rústicas"], status: "preparando", color: "orange" },
      { mesa: "Para llevar", time: "00:08", items: ["3x Tacos pastor"], status: "nuevo", color: "emerald" },
      { mesa: "Mesa 07", time: "03:42", items: ["Pizza pepperoni"], status: "listo", color: "zinc" },
    ].map((t, i) => (
      <motion.div
        key={t.mesa}
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: false }}
        transition={{ delay: 0.1 + i * 0.08, duration: 0.5 }}
        className={`rounded-xl p-4 ring-1 shadow-sm bg-white ${
          t.color === "orange"
            ? "ring-orange-300 bg-orange-50/40"
            : t.color === "emerald"
            ? "ring-emerald-300 bg-emerald-50/50"
            : "ring-zinc-300 bg-zinc-50"
        }`}
      >
        <div className="flex items-center justify-between">
          <div className="font-display font-bold text-zinc-950 text-sm">{t.mesa}</div>
          <div className="text-[10px] font-mono text-zinc-500 flex items-center gap-1">
            <Clock4 className="w-3 h-3" /> {t.time}
          </div>
        </div>
        <ul className="mt-3 space-y-1">
          {t.items.map((it) => (
            <li key={it} className="text-xs text-zinc-700">• {it}</li>
          ))}
        </ul>
        <div
          className={`mt-3 inline-block text-[9px] uppercase tracking-[0.18em] font-bold px-2 py-1 rounded-full ${
            t.color === "orange"
              ? "bg-orange-500 text-white"
              : t.color === "emerald"
              ? "bg-emerald-700 text-white"
              : "bg-zinc-900 text-white"
          }`}
        >
          {t.status}
        </div>
      </motion.div>
    ))}
  </div>
);

const Cocina = ({ id, index, total }) => (
  <TwoCol id={id} index={index} total={total} bg="muted" reverse>
    <div className="lg:col-span-6">
      <SectionHeading
        eyebrow="06 · Cocina"
        title="Monitor de cocina"
        accentWord="en tiempo real."
        lead="Los pedidos llegan automáticamente a cocina para mejorar la comunicación entre meseros, caja y cocina. Menos errores, más velocidad."
      />
      <Reveal delay={0.3}>
        <ul className="mt-8 space-y-3.5">
          <FeatureItem icon={CheckCircle2}>Menos errores de comunicación.</FeatureItem>
          <FeatureItem icon={ShieldCheck}>Mejor organización por estación.</FeatureItem>
          <FeatureItem icon={Clock4}>Pedidos cronometrados en vivo.</FeatureItem>
          <FeatureItem icon={Flame}>Mayor velocidad de preparación.</FeatureItem>
        </ul>
      </Reveal>
    </div>
    <div className="lg:col-span-6">
      <Reveal delay={0.2}><KitchenMockup /></Reveal>
    </div>
  </TwoCol>
);

/* ============ SLIDE 7 — CAJA Y ARQUEOS ============ */

const CashMockup = () => (
  <div className="relative max-w-md mx-auto">
    <div className="rounded-3xl bg-white ring-1 ring-zinc-200 shadow-xl p-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Corte del día</div>
          <div className="font-display font-black text-zinc-950 text-3xl mt-1">$12,840.00</div>
        </div>
        <div className="grid place-items-center w-10 h-10 rounded-xl bg-emerald-900 text-white">
          <Wallet className="w-5 h-5" />
        </div>
      </div>

      <div className="mt-6 space-y-3">
        {[
          { icon: Banknote, label: "Efectivo", value: "$5,420.00", color: "emerald" },
          { icon: CreditCard, label: "Tarjetas", value: "$4,920.00", color: "orange" },
          { icon: Smartphone, label: "Transferencia", value: "$1,800.00", color: "zinc" },
          { icon: Receipt, label: "Vales / Otros", value: "$700.00", color: "zinc" },
        ].map((m, i) => (
          <motion.div
            key={m.label}
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false }}
            transition={{ delay: 0.1 + i * 0.06 }}
            className="flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <span
                className={`grid place-items-center w-8 h-8 rounded-lg ${
                  m.color === "emerald" ? "bg-emerald-50 text-emerald-800" : m.color === "orange" ? "bg-orange-50 text-orange-600" : "bg-zinc-100 text-zinc-700"
                }`}
              >
                <m.icon className="w-4 h-4" />
              </span>
              <div className="text-sm font-medium text-zinc-950">{m.label}</div>
            </div>
            <div className="font-display font-bold text-sm text-zinc-950">{m.value}</div>
          </motion.div>
        ))}
      </div>

      <div className="mt-6 pt-5 border-t border-dashed border-zinc-200 flex items-center justify-between">
        <div className="text-xs text-zinc-500">Ventas · 84 tickets</div>
        <button className="text-xs font-bold uppercase tracking-[0.16em] text-orange-600">
          Arqueo automático
        </button>
      </div>
    </div>
  </div>
);

const Caja = ({ id, index, total }) => (
  <TwoCol id={id} index={index} total={total} bg="white">
    <div className="lg:col-span-6">
      <SectionHeading
        eyebrow="07 · Caja"
        title="Caja con corte y arqueo"
        accentWord="automático."
        lead="Controla ingresos, métodos de pago, movimientos de caja, cortes y arqueos de forma automática. Cierra el día en minutos."
      />
      <Reveal delay={0.3}>
        <ul className="mt-8 space-y-3.5">
          <FeatureItem icon={Banknote}>Efectivo, tarjetas, transferencias y vales.</FeatureItem>
          <FeatureItem icon={ShieldCheck}>Cortes parciales y de fin de turno.</FeatureItem>
          <FeatureItem icon={Receipt}>Resumen de ventas e impuestos.</FeatureItem>
          <FeatureItem icon={CheckCircle2}>Conciliación clara y sin errores.</FeatureItem>
        </ul>
      </Reveal>
    </div>
    <div className="lg:col-span-6">
      <Reveal delay={0.2}><CashMockup /></Reveal>
    </div>
  </TwoCol>
);

/* ============ SLIDE 8 — MENÚ DIGITAL QR ============ */

const QRMockup = () => (
  <div className="relative max-w-md mx-auto flex justify-center">
    {/* Phone */}
    <div className="relative w-[260px] rounded-[36px] bg-zinc-950 p-2.5 shadow-2xl ring-1 ring-zinc-900/10">
      <div className="rounded-[28px] bg-white overflow-hidden">
        <div className="relative h-32 bg-gradient-to-br from-emerald-900 to-emerald-700 p-4 text-white">
          <div className="text-[10px] uppercase tracking-[0.22em] opacity-70">Menú</div>
          <div className="font-display font-black text-2xl mt-1">La Casa del Chef</div>
          <div className="text-xs opacity-80 mt-1">Cocina mexicana contemporánea</div>
        </div>
        <div className="p-4 space-y-3">
          {[
            { name: "Aguachile verde", price: "$220", icon: Soup },
            { name: "Tostadas de atún", price: "$185", icon: Utensils },
            { name: "Mezcal Tobalá 1.5oz", price: "$120", icon: Wine },
          ].map((it, i) => (
            <motion.div
              key={it.name}
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false }}
              transition={{ delay: 0.1 + i * 0.08 }}
              className="flex items-center justify-between"
            >
              <div className="flex items-center gap-2.5">
                <span className="grid place-items-center w-8 h-8 rounded-lg bg-zinc-100 text-zinc-700">
                  <it.icon className="w-4 h-4" />
                </span>
                <div className="text-xs font-semibold text-zinc-950">{it.name}</div>
              </div>
              <div className="text-xs font-display font-bold text-emerald-800">{it.price}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
    {/* QR card */}
    <motion.div
      animate={{ rotate: [-3, 1, -3] }}
      transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      className="absolute -left-6 bottom-6 bg-white rounded-2xl ring-1 ring-zinc-200 shadow-xl p-3 hidden sm:block"
    >
      <div className="w-20 h-20 grid place-items-center bg-zinc-950 rounded-lg">
        <QrCode className="w-12 h-12 text-white" />
      </div>
      <div className="mt-2 text-center text-[9px] uppercase tracking-[0.18em] font-bold text-zinc-700">
        Escanea
      </div>
    </motion.div>
  </div>
);

const MenuQR = ({ id, index, total }) => (
  <TwoCol id={id} index={index} total={total} bg="muted" reverse>
    <div className="lg:col-span-6">
      <SectionHeading
        eyebrow="08 · Menú digital"
        title="Menú QR y carta digital"
        accentWord="sin costo adicional."
        lead="Tus clientes consultan el menú desde su celular escaneando un código QR. Actualización inmediata, imagen moderna, cero reimpresiones."
      />
      <Reveal delay={0.3}>
        <ul className="mt-8 space-y-3.5">
          <FeatureItem icon={TrendingDown}>Sin impresión ni reimpresión de menús.</FeatureItem>
          <FeatureItem icon={Zap}>Actualización inmediata de precios y platillos.</FeatureItem>
          <FeatureItem icon={Smartphone}>Imagen moderna en restaurantes, bares y cafés.</FeatureItem>
          <FeatureItem icon={Beer}>Ideal para barras y altas rotaciones.</FeatureItem>
        </ul>
      </Reveal>
    </div>
    <div className="lg:col-span-6">
      <Reveal delay={0.2}><QRMockup /></Reveal>
    </div>
  </TwoCol>
);

/* ============ SLIDE 9 — PÁGINA WEB DELIVERY ============ */

const WebMockup = () => (
  <div className="relative max-w-xl mx-auto">
    <div className="rounded-2xl bg-zinc-900 p-2.5 shadow-2xl">
      <div className="flex items-center gap-1.5 px-3 py-2">
        <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
        <span className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
        <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
        <div className="ml-3 px-3 py-1 rounded-md bg-zinc-800 text-zinc-400 text-[10px] font-mono">
          micomida.erpalimentos.com.mx
        </div>
      </div>
      <div className="rounded-xl bg-white overflow-hidden">
        <div className="relative h-32 bg-[linear-gradient(135deg,#064E3B_0%,#047857_100%)] p-5 text-white">
          <div className="text-[10px] uppercase tracking-[0.22em] opacity-70">Pedido propio</div>
          <div className="font-display font-black text-2xl mt-1">Pizza Romana</div>
          <div className="text-xs opacity-80 mt-1">Entrega en 25-35 min · Sin comisiones</div>
        </div>
        <div className="p-4 grid grid-cols-3 gap-2">
          {[
            { name: "Margherita", price: "$180", icon: Pizza },
            { name: "Diavola", price: "$210", icon: Pizza },
            { name: "Cuatro quesos", price: "$220", icon: Pizza },
          ].map((p, i) => (
            <motion.div
              key={p.name}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false }}
              transition={{ delay: 0.1 + i * 0.08 }}
              className="rounded-xl ring-1 ring-zinc-200 p-3"
            >
              <div className="aspect-square rounded-lg bg-orange-50 grid place-items-center">
                <p.icon className="w-7 h-7 text-orange-500" />
              </div>
              <div className="mt-2 text-[11px] font-semibold text-zinc-950 truncate">{p.name}</div>
              <div className="text-[11px] font-display font-bold text-emerald-800">{p.price}</div>
            </motion.div>
          ))}
        </div>
        <div className="px-4 pb-4 flex items-center justify-between">
          <div className="text-[10px] text-zinc-500">Pago en efectivo, tarjeta o transferencia</div>
          <button className="rounded-full bg-orange-500 text-white text-[11px] font-bold px-4 py-2">
            Pedir ahora
          </button>
        </div>
      </div>
    </div>
  </div>
);

const WebDelivery = ({ id, index, total }) => (
  <TwoCol id={id} index={index} total={total} bg="white">
    <div className="lg:col-span-6">
      <SectionHeading
        eyebrow="09 · Página web"
        title="Tu página web delivery"
        accentWord="sin comisiones."
        lead="Cada restaurante puede tener su propia página para recibir pedidos directos. Recibe pedidos propios y conserva tus ganancias."
      />
      <Reveal delay={0.3}>
        <ul className="mt-8 space-y-3.5">
          <FeatureItem icon={Globe}>Dominio personalizable de tu marca.</FeatureItem>
          <FeatureItem icon={ShieldCheck}>0% comisiones por venta.</FeatureItem>
          <FeatureItem icon={CreditCard}>Pago en línea o contra entrega.</FeatureItem>
          <FeatureItem icon={CheckCircle2}>Cero costo adicional incluido.</FeatureItem>
        </ul>
      </Reveal>
    </div>
    <div className="lg:col-span-6">
      <Reveal delay={0.2}><WebMockup /></Reveal>
    </div>
  </TwoCol>
);

/* ============ SLIDE 10 — INVENTARIOS ============ */

const InventoryMockup = () => (
  <div className="max-w-md mx-auto bg-white rounded-3xl ring-1 ring-zinc-200 shadow-xl overflow-hidden">
    <div className="px-5 py-4 bg-zinc-950 text-white flex items-center justify-between">
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] opacity-60">Inventario · Almacén central</div>
        <div className="font-display font-bold text-base mt-0.5">324 SKUs activos</div>
      </div>
      <Boxes className="w-5 h-5 text-orange-400" />
    </div>
    <div className="divide-y divide-zinc-100">
      {[
        { name: "Tomate saladet", stock: 12, unit: "kg", status: "ok" },
        { name: "Tortilla de maíz", stock: 4, unit: "kg", status: "low" },
        { name: "Carne sirloin", stock: 28, unit: "kg", status: "ok" },
        { name: "Cilantro fresco", stock: 0.5, unit: "kg", status: "critical" },
      ].map((it, i) => (
        <motion.div
          key={it.name}
          initial={{ opacity: 0, x: -10 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: false }}
          transition={{ delay: 0.1 + i * 0.07 }}
          className="px-5 py-3 flex items-center justify-between"
        >
          <div>
            <div className="text-sm font-semibold text-zinc-950">{it.name}</div>
            <div className="text-[11px] text-zinc-500">{it.stock} {it.unit} en stock</div>
          </div>
          <span
            className={`text-[10px] uppercase tracking-[0.16em] font-bold px-2 py-1 rounded-full ${
              it.status === "ok"
                ? "bg-emerald-50 text-emerald-700"
                : it.status === "low"
                ? "bg-orange-50 text-orange-700"
                : "bg-red-50 text-red-700"
            }`}
          >
            {it.status === "ok" ? "Suficiente" : it.status === "low" ? "Bajo" : "Crítico"}
          </span>
        </motion.div>
      ))}
    </div>
    <div className="px-5 py-3 bg-orange-50 text-orange-700 text-xs font-medium flex items-center gap-2">
      <AlertTriangle className="w-4 h-4" />
      Alerta: 2 productos requieren compra inmediata.
    </div>
  </div>
);

const Inventarios = ({ id, index, total }) => (
  <TwoCol id={id} index={index} total={total} bg="muted" reverse>
    <div className="lg:col-span-6">
      <SectionHeading
        eyebrow="10 · Inventarios"
        title="Control total de inventarios y"
        accentWord="existencias."
        lead="Controla ingredientes, productos, entradas, salidas, existencias y movimientos de almacén con alertas inteligentes."
      />
      <Reveal delay={0.3}>
        <ul className="mt-8 space-y-3.5">
          <FeatureItem icon={ShieldCheck}>Evita pérdidas y mermas.</FeatureItem>
          <FeatureItem icon={CheckCircle2}>Controla compras a proveedores.</FeatureItem>
          <FeatureItem icon={AlertTriangle}>Detecta faltantes antes de servir.</FeatureItem>
          <FeatureItem icon={Boxes}>Multi-almacén y multi-sucursal.</FeatureItem>
        </ul>
      </Reveal>
    </div>
    <div className="lg:col-span-6">
      <Reveal delay={0.2}><InventoryMockup /></Reveal>
    </div>
  </TwoCol>
);

export const SLIDES_PART_B = {
  pos: POS,
  cocina: Cocina,
  caja: Caja,
  "menu-qr": MenuQR,
  "web-delivery": WebDelivery,
  inventarios: Inventarios,
};
